import React, { useState } from 'react';
import { X, Eye, CheckCircle, Edit, ExternalLink, Download, Image as ImageIcon, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';

export const SubmissionPreview = ({ 
  formData, 
  category, 
  isOpen, 
  onClose, 
  onEdit, 
  onSubmit,
  submitting 
}) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [pdfViewerName, setPdfViewerName] = useState('');

  // Helper function to extract only images (not PDFs) from file arrays
  const extractImages = (fileArray) => {
    if (!Array.isArray(fileArray)) return [];
    
    return fileArray.filter((file) => {
      if (file instanceof File) {
        return file.type.startsWith('image/');
      } else if (typeof file === 'string') {
        return file.startsWith('data:image/') || (!file.startsWith('data:application/pdf'));
      }
      return false;
    });
  };

  const categoryData = formData?.categorySpecificData?.[formData.selectedCategories?.[0]] || {};
  const photos = categoryData.photos || [];
  
  // For innovation category - multiple photo galleries
  const conceptPhotos = categoryData.concept_process_photos || [];
  const finalPhotos = categoryData.final_build_photography || [];
  const detailPhotos = categoryData.innovation_detail_photos || [];
  
  // For technology category - multiple photo galleries (extract only images, not PDFs)
  const techSetupPhotos = extractImages(categoryData.tech_setup_photos || []);
  const techActionPhotos = extractImages(categoryData.tech_action_photos || []);
  const uiScreenshots = extractImages(categoryData.ui_screenshots || []);
  const architectureDiagramImages = extractImages(categoryData.architecture_diagram || []);
  const uxFlowDiagramsImages = extractImages(categoryData.ux_flow_diagrams || []);
  const analyticsReportImages = extractImages(categoryData.analytics_report || []);
  const heatmapsBehaviorImages = extractImages(categoryData.heatmaps_behavior || []);
  const allInnovationPhotos = [...conceptPhotos, ...finalPhotos, ...detailPhotos];

  // Get PDF documents from technical documentation and prototypes fields (innovation)
  const technicalDocs = categoryData.technical_documentation || [];
  const prototypeDocs = categoryData.prototypes || [];
  
  // Get PDF documents for technology category (both from dedicated and photo fields) - use original arrays
  const architectureDiagram = categoryData.architecture_diagram || [];
  const uxFlowDiagrams = categoryData.ux_flow_diagrams || [];
  const analyticsReport = categoryData.analytics_report || [];
  const heatmapsBehavior = categoryData.heatmaps_behavior || [];
  const techSetupAll = categoryData.tech_setup_photos || [];
  const techActionAll = categoryData.tech_action_photos || [];
  const uiScreenshotsAll = categoryData.ui_screenshots || [];
  
  // Helper function to extract PDFs from file arrays
  const extractPDFs = (fileArray, label) => {
    if (!Array.isArray(fileArray)) return [];
    
    return fileArray.map((file, index) => {
      const isPDF = file instanceof File 
        ? file.type === 'application/pdf'
        : typeof file === 'string' && file.startsWith('data:application/pdf');
      
      if (!isPDF) return null;
      
      const fileName = file instanceof File 
        ? file.name 
        : `${label}_${index + 1}.pdf`;
      
      const fileSize = file instanceof File 
        ? (file.size / (1024 * 1024)).toFixed(2) + ' MB'
        : 'Unknown size';
      
      const fileUrl = file instanceof File 
        ? URL.createObjectURL(file)
        : file;
      
      return { fileName, fileSize, fileUrl, label };
    }).filter(Boolean);
  };
  
  const technicalPDFs = extractPDFs(technicalDocs, 'Technical Documentation');
  const prototypePDFs = extractPDFs(prototypeDocs, 'Prototype');
  
  // Technology category PDFs (from all fields including photo fields)
  const architecturePDFs = extractPDFs(architectureDiagram, 'Architecture Diagram');
  const uxFlowPDFs = extractPDFs(uxFlowDiagrams, 'UX Flow Diagrams');
  const analyticsPDFs = extractPDFs(analyticsReport, 'Analytics Report');
  const heatmapPDFs = extractPDFs(heatmapsBehavior, 'Heatmaps & Behavior Analysis');
  const techSetupPDFs = extractPDFs(techSetupAll, 'Tech Setup Documentation');
  const techActionPDFs = extractPDFs(techActionAll, 'Tech Action Documentation');
  const uiScreenshotPDFs = extractPDFs(uiScreenshotsAll, 'UI Documentation');
  
  const allPDFs = category?.id === 'technology' 
    ? [...architecturePDFs, ...uxFlowPDFs, ...analyticsPDFs, ...heatmapPDFs, ...techSetupPDFs, ...techActionPDFs, ...uiScreenshotPDFs]
    : [...technicalPDFs, ...prototypePDFs];

  // Lightbox navigation functions
  const openLightbox = (index, photoArray = photos) => {
    const photo = photoArray[index];
    const imageSrc = photo instanceof File 
      ? URL.createObjectURL(photo)
      : typeof photo === 'string' ? photo : null;
    if (imageSrc) {
      setLightboxImage(imageSrc);
      setLightboxIndex(index);
      setZoomLevel(1);
    }
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setZoomLevel(1);
  };

  const nextImage = () => {
    if (lightboxIndex < photos.length - 1) {
      openLightbox(lightboxIndex + 1);
    }
  };

  const prevImage = () => {
    if (lightboxIndex > 0) {
      openLightbox(lightboxIndex - 1);
    }
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.5, 0.5));

  // Keyboard navigation
  React.useEffect(() => {
    if (!lightboxImage) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight' || e.key === ' ') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-' || e.key === '_') handleZoomOut();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex]);

  // Helper to display non-empty fields
  const renderField = (label, value, isLarge = false, isQuote = false) => {
    if (!value || (typeof value === 'string' && !value.trim())) return null;
    
    // Quote style for testimonials
    if (isQuote) {
      return (
        <div className="my-4 bg-gray-50 border-l-4 border-orange-500 rounded-r px-5 py-4">
          <p className="text-gray-900 italic leading-relaxed text-sm mb-2">
            "{value}"
          </p>
          {label && (
            <p className="text-orange-600 font-semibold text-sm not-italic">
              — {label}
            </p>
          )}
        </div>
      );
    }
    
    return (
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
          {label}
        </h4>
        <p className={`text-gray-900 whitespace-pre-wrap leading-relaxed ${isLarge ? 'text-base' : 'text-sm'}`}>
          {value}
        </p>
      </div>
    );
  };

  const renderSection = (title, children, icon = null) => {
    // Check if section has any content
    const hasContent = React.Children.toArray(children).some(child => child !== null);
    if (!hasContent) return null;

    return (
      <div className="mb-10 pb-8 border-b-2 border-gray-200 last:border-b-0">
        <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wider mb-5 pb-3 border-b-3 border-orange-500">
          {title}
        </h3>
        <div className="space-y-6">
          {children}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;
  if (!category) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-lg shadow-2xl w-full max-w-6xl my-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-lg z-10">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  {category.icon && React.createElement(category.icon, { className: 'h-8 w-8 text-orange-600' })}
                  {!category.icon && <Eye className="h-8 w-8 text-orange-600" />}
                  <h2 className="text-3xl font-bold text-gray-900">{category.title}</h2>
                </div>
                <p className="text-sm text-gray-600">Submission Preview - Review before submitting</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex gap-2 border-b border-gray-200">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'details', label: 'Details' },
                { id: 'technical', label: 'Technical' },
                { id: 'team', label: 'Team & Impact' },
                { id: 'documents', label: 'Documents', show: allPDFs.length > 0 },
                { id: 'files', label: 'Files & Media' }
              ].filter(tab => tab.show !== false).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 font-semibold text-sm transition-all relative ${
                    activeTab === tab.id
                      ? 'text-orange-600 border-b-2 border-orange-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {tab.id === 'documents' && allPDFs.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full">
                      {allPDFs.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
            {/* Tab: Overview */}
            {activeTab === 'overview' && (
              <>
                {/* Project Header - Info Box */}
                <div className="mb-8 p-6 bg-orange-50 rounded-lg border border-orange-200 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {categoryData.project_name || formData.projectName || 'Untitled Project'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {categoryData.exhibition_info && (
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[100px]">Exhibition:</span>
                        <span className="text-gray-900">{categoryData.exhibition_info}</span>
                      </div>
                    )}
                    {categoryData.stand_area && (
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[100px]">Stand Area:</span>
                        <span className="text-gray-900">{categoryData.stand_area} m²</span>
                      </div>
                    )}
                    {categoryData.client_brand_name && (
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[100px]">Client/Brand:</span>
                        <span className="text-gray-900">{categoryData.client_brand_name}</span>
                      </div>
                    )}
                    {categoryData.budget_range && (
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[100px]">Budget:</span>
                        <span className="text-gray-900">{categoryData.budget_range}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-gray-700 min-w-[100px]">Company:</span>
                      <span className="text-gray-900">{formData.company}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                {renderSection(
                  'Contact Information',
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderField('Name', `${formData.firstName} ${formData.lastName}`)}
                    {renderField('Email', formData.email)}
                    {renderField('Phone', formData.phone)}
                    {renderField('Company', formData.company)}
                    {renderField('Country', formData.country)}
                  </div>
                )}
              </>
            )}

            {/* Tab: Details */}
            {activeTab === 'details' && (
              <>
                {/* Innovation Details (for innovation category) */}
                {category.id === 'innovation' && renderSection(
                  'Innovation Details',
                  <>
                    {renderField('Innovation Description', categoryData.innovation_description, true)}
                    {renderField('Problem Statement', categoryData.problem_statement, true)}
                    {renderField('Technology/Materials/Methods Used', categoryData.tech_materials_methods, true)}
                    {renderField('Comparison with Traditional Approaches', categoryData.comparison_traditional, true)}
                  </>
                )}

                {/* Concept & Design (for creative category) */}
                {category.id === 'creative' && renderSection(
                  'Concept & Design',
                  <>
                    {renderField('Concept Statement', categoryData.concept_statement, true)}
                    {renderField('Design Philosophy', categoryData.design_philosophy, true)}
                    {renderField('Target Audience', categoryData.target_audience)}
                    {renderField('Main Theme & Message', categoryData.main_theme, true)}
                    {renderField('Creative Process Story', categoryData.creative_story, true)}
                  </>
                )}

                {/* Sustainability Details (for innovation category) */}
                {category.id === 'innovation' && (categoryData.sustainability_features || categoryData.sustainability_description) && renderSection(
                  'Sustainability & Social Impact',
                  <>
                    {categoryData.sustainability_features && Array.isArray(categoryData.sustainability_features) && categoryData.sustainability_features.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                          Sustainability Features
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryData.sustainability_features.map((feature, idx) => (
                            <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                              ✓ {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {renderField('Sustainability Description', categoryData.sustainability_description, true)}
                  </>
                )}

                {/* Technology Overview (for technology category) */}
                {category.id === 'technology' && renderSection(
                  'Technology Overview',
                  <>
                    {renderField('Technology Concept & Vision', categoryData.technology_concept, true)}
                    {renderField('Technical Challenge Addressed', categoryData.technical_challenge, true)}
                    {renderField('Innovation & Uniqueness', categoryData.innovation_uniqueness, true)}
                    {categoryData.primary_technology_category && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                          Primary Technology
                        </h4>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                          {categoryData.primary_technology_category}
                        </span>
                        {categoryData.other_technology && (
                          <span className="ml-2 text-sm text-gray-600">
                            ({categoryData.other_technology})
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* User Experience (for technology category) */}
                {category.id === 'technology' && renderSection(
                  'User Experience & Interaction',
                  <>
                    {renderField('User Journey & Flow', categoryData.user_journey, true)}
                    {renderField('Interaction Details', categoryData.interaction_details, true)}
                    {renderField('Accessibility Features', categoryData.accessibility_features)}
                    {categoryData.interaction_methods && Array.isArray(categoryData.interaction_methods) && categoryData.interaction_methods.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                          Interaction Methods
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryData.interaction_methods.map((method, idx) => (
                            <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                              ✓ {method}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Tab: Technical */}
            {activeTab === 'technical' && (
              <>
                {/* Technical Architecture (for technology category) */}
                {category.id === 'technology' && renderSection(
                  'Technical Architecture',
                  <>
                    {renderField('Technical Architecture Overview', categoryData.technical_architecture, true)}
                    {renderField('Hardware Specifications', categoryData.hardware_specifications, true)}
                    {renderField('Software & Platforms', categoryData.software_platforms, true)}
                    {renderField('Integration & APIs', categoryData.integration_apis)}
                  </>
                )}

                {/* Technical Implementation (for technology category) */}
                {category.id === 'technology' && renderSection(
                  'Technical Implementation & Challenges',
                  <>
                    {renderField('Development Process & Timeline', categoryData.development_process, true)}
                    {renderField('Technical Challenges & Solutions', categoryData.technical_challenges, true)}
                    {renderField('Performance Optimization', categoryData.performance_optimization)}
                    {renderField('Security & Privacy Measures', categoryData.security_privacy)}
                  </>
                )}

                {/* Technical Information (for other categories) */}
                {category.id !== 'technology' && renderSection(
                  'Technical Information',
                  <>
                    {renderField('Materials Used', categoryData.materials_used, true)}
                    {renderField('Custom Fabrication Details', categoryData.custom_fabrication, true)}
                    {renderField('Technical Challenges & Solutions', categoryData.technical_challenges, true)}
                    {renderField('Material Sourcing', categoryData.material_sourcing)}
                    {renderField('Construction Methods', categoryData.construction_methods)}
                    {renderField('Reuse Strategy', categoryData.reuse_strategy)}
                  </>
                )}

                {/* Measurable Results (for innovation category) */}
                {category.id === 'innovation' && renderSection(
                  'Measurable Results & Impact',
                  <>
                    {renderField('Impact Metrics', categoryData.impact_metrics, true)}
                    {renderField('Visitor Engagement Metrics', categoryData.visitor_metrics, true)}
                    {renderField('ROI & Business Impact', categoryData.roi_business_impact, true)}
                    {categoryData.client_feedback && renderField('Client Feedback', categoryData.client_feedback, false, true)}
                  </>
                )}

                {/* Performance & Analytics (for technology category) */}
                {category.id === 'technology' && renderSection(
                  'Performance & Analytics',
                  <>
                    {renderField('Visitor Engagement Metrics', categoryData.visitor_engagement_metrics, true)}
                    {renderField('Technology Performance Data', categoryData.technology_performance, true)}
                    {renderField('Business Impact & ROI', categoryData.business_impact)}
                  </>
                )}

                {/* Sustainability & Scalability (for technology category) */}
                {category.id === 'technology' && (categoryData.energy_efficiency || categoryData.scalability_reusability) && renderSection(
                  'Sustainability & Scalability',
                  <>
                    {renderField('Energy Efficiency & Environmental Impact', categoryData.energy_efficiency)}
                    {renderField('Scalability & Reusability', categoryData.scalability_reusability)}
                  </>
                )}
              </>
            )}

            {/* Tab: Documents */}
            {activeTab === 'documents' && (
              <>
                {allPDFs.length > 0 && renderSection(
                  'Technical Documents',
                  <>
                    <p className="text-gray-600 mb-4">Total: {allPDFs.length} document(s)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {allPDFs.map((pdf, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:shadow-md transition-all"
                          data-testid={`pdf-card-${index}`}
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <div className="flex-shrink-0">
                              <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center">
                                <FileText className="h-6 w-6 text-red-600" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-gray-900 truncate" title={pdf.fileName}>
                                {pdf.fileName}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">{pdf.fileSize}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{pdf.label}</p>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                              onClick={() => {
                                setPdfViewerUrl(pdf.fileUrl);
                                setPdfViewerName(pdf.fileName);
                                setPdfViewerOpen(true);
                              }}
                              data-testid={`preview-pdf-${index}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Preview
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = pdf.fileUrl;
                                link.download = pdf.fileName;
                                link.click();
                              }}
                              data-testid={`download-pdf-${index}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Tab: Team & Impact */}
            {activeTab === 'team' && (
              <>
                {/* Impact & Results */}
                {renderSection(
                  'Impact & Results',
                  <>
                    {renderField('Visitor Engagement & Numbers', categoryData.visitor_engagement, true)}
                    {categoryData.client_feedback && renderField('Client Testimonial', categoryData.client_feedback, false, true)}
                    {renderField('Awards & Recognition', categoryData.awards)}
                    {renderField('Awards & Recognition', categoryData.awards_recognition)}
                  </>
                )}

                {/* Team Information */}
                {renderSection(
                  'Team & Collaborations',
                  <>
                    {category.id === 'technology' ? (
                      <>
                        {renderField('Technology Development Team', categoryData.development_team, true)}
                        {renderField('Technology Partners & Vendors', categoryData.tech_partners, true)}
                      </>
                    ) : (
                      <>
                        {renderField('Design Team Credits', categoryData.design_team, true)}
                        {renderField('Production Team Credits', categoryData.production_team, true)}
                        {renderField('Partners & Suppliers', categoryData.partners_suppliers, true)}
                        {renderField('Project Partners & Collaborators', categoryData.project_partners, true)}
                      </>
                    )}
                  </>
                )}

                {/* Additional Documentation */}
                {(categoryData.media_coverage) && renderSection(
                  'Media Coverage',
                  <>
                    {categoryData.media_coverage && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                          Media Links
                        </h4>
                        <div className="space-y-2">
                          {categoryData.media_coverage.split('\n').filter(line => line.trim()).map((link, idx) => (
                            <a
                              key={idx}
                              href={link.trim()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span className="break-all">{link.trim()}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Tab: Files & Media */}
            {activeTab === 'files' && (
              <>
                {/* Innovation Category - Multiple Photo Galleries */}
                {category.id === 'innovation' && (
                  <>
                    {/* Concept & Process Photos */}
                    {conceptPhotos.length > 0 && renderSection(
                      'Concept & Process Photos',
                      <>
                        <p className="text-gray-600 mb-4">Total: {conceptPhotos.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {conceptPhotos.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, conceptPhotos)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Concept photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Final Build Photography */}
                    {finalPhotos.length > 0 && renderSection(
                      'Final Build Photography',
                      <>
                        <p className="text-gray-600 mb-4">Total: {finalPhotos.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {finalPhotos.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, finalPhotos)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Final build photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Innovation Detail Photos */}
                    {detailPhotos.length > 0 && renderSection(
                      'Innovation Detail Photos',
                      <>
                        <p className="text-gray-600 mb-4">Total: {detailPhotos.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {detailPhotos.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, detailPhotos)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Detail photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Technology Category - Multiple Photo Galleries */}
                {category.id === 'technology' && (
                  <>
                    {/* Technology Setup & Behind-the-Scenes Photos */}
                    {techSetupPhotos.length > 0 && renderSection(
                      'Technology Setup & Behind-the-Scenes Photos',
                      <>
                        <p className="text-gray-600 mb-4">Total: {techSetupPhotos.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {techSetupPhotos.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, techSetupPhotos)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Setup photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Technology in Action Photos */}
                    {techActionPhotos.length > 0 && renderSection(
                      'Technology in Action Photos',
                      <>
                        <p className="text-gray-600 mb-4">Total: {techActionPhotos.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {techActionPhotos.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, techActionPhotos)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Technology action photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* UI/UX Screenshots */}
                    {uiScreenshots.length > 0 && renderSection(
                      'UI/UX Screenshots',
                      <>
                        <p className="text-gray-600 mb-4">Total: {uiScreenshots.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {uiScreenshots.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, uiScreenshots)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`UI screenshot ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Technical Architecture Diagram Images */}
                    {architectureDiagramImages.length > 0 && renderSection(
                      'Technical Architecture Diagram',
                      <>
                        <p className="text-gray-600 mb-4">Total: {architectureDiagramImages.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {architectureDiagramImages.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, architectureDiagramImages)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Architecture diagram ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* UX Flow Diagrams Images */}
                    {uxFlowDiagramsImages.length > 0 && renderSection(
                      'UX Flow Diagrams / Wireframes',
                      <>
                        <p className="text-gray-600 mb-4">Total: {uxFlowDiagramsImages.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {uxFlowDiagramsImages.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, uxFlowDiagramsImages)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`UX flow diagram ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Post-Event Analytics Report Images */}
                    {analyticsReportImages.length > 0 && renderSection(
                      'Post-Event Analytics Report',
                      <>
                        <p className="text-gray-600 mb-4">Total: {analyticsReportImages.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {analyticsReportImages.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, analyticsReportImages)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Analytics report ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Heatmaps & Behavior Analysis Images */}
                    {heatmapsBehaviorImages.length > 0 && renderSection(
                      'Heatmaps & Behavior Analysis',
                      <>
                        <p className="text-gray-600 mb-4">Total: {heatmapsBehaviorImages.length} photo(s)</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {heatmapsBehaviorImages.map((photo, index) => {
                            const imageSrc = photo instanceof File 
                              ? URL.createObjectURL(photo)
                              : typeof photo === 'string' ? photo : null;
                            
                            if (!imageSrc) return null;

                            return (
                              <div
                                key={index}
                                className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                                onClick={() => openLightbox(index, heatmapsBehaviorImages)}
                              >
                                <img
                                  src={imageSrc}
                                  alt={`Heatmap ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                  Photo {index + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Other Categories - Single Photo Gallery */}
                {category.id !== 'innovation' && category.id !== 'technology' && photos.length > 0 && renderSection(
                  'Project Photos',
                  <>
                    <p className="text-gray-600 mb-4">Total: {photos.length} photo(s)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {photos.map((photo, index) => {
                        const imageSrc = photo instanceof File 
                          ? URL.createObjectURL(photo)
                          : typeof photo === 'string' ? photo : null;
                        
                        if (!imageSrc) return null;

                        return (
                          <div
                            key={index}
                            className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-orange-500 transition-all cursor-pointer group shadow-sm hover:shadow-lg"
                            onClick={() => openLightbox(index)}
                          >
                            <img
                              src={imageSrc}
                              alt={`Project photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <Eye className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                            </div>
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                              Photo {index + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Video & Virtual Tour Section */}
                {(categoryData.video_link || categoryData.video_showcase_url || categoryData.virtual_tour_url || 
                  categoryData.demo_video_primary || categoryData.demo_video_short || categoryData.user_testing_video || categoryData.virtual_demo_url) && renderSection(
                  'Media Links',
                  <>
                    <div className="space-y-4">
                      {/* Technology Category - Multiple Video Links */}
                      {category.id === 'technology' && (
                        <>
                          {categoryData.demo_video_primary && (
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                              <div className="flex items-start gap-3">
                                <div className="text-3xl">🎬</div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-blue-900 mb-1">Technology Demo Video (Primary)</h4>
                                  <p className="text-sm text-blue-700 mb-3">Main demonstration video (2-5 minutes)</p>
                                  <a 
                                    href={categoryData.demo_video_primary}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                  >
                                    <span>▶</span>
                                    <span>Watch Primary Demo</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {categoryData.demo_video_short && (
                            <div className="p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                              <div className="flex items-start gap-3">
                                <div className="text-3xl">⚡</div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-indigo-900 mb-1">Technology Demo Video (Short)</h4>
                                  <p className="text-sm text-indigo-700 mb-3">Quick overview (≤2 minutes)</p>
                                  <a 
                                    href={categoryData.demo_video_short}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
                                  >
                                    <span>▶</span>
                                    <span>Watch Short Demo</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {categoryData.user_testing_video && (
                            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                              <div className="flex items-start gap-3">
                                <div className="text-3xl">👥</div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-green-900 mb-1">User Testing / Feedback Video</h4>
                                  <p className="text-sm text-green-700 mb-3">Real user interactions and feedback</p>
                                  <a 
                                    href={categoryData.user_testing_video}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                                  >
                                    <span>▶</span>
                                    <span>Watch User Testing</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {categoryData.virtual_demo_url && (
                            <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                              <div className="flex items-start gap-3">
                                <div className="text-3xl">🎮</div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-purple-900 mb-1">Virtual Tour / Interactive Demo</h4>
                                  <p className="text-sm text-purple-700 mb-3">Explore the interactive experience</p>
                                  <a 
                                    href={categoryData.virtual_demo_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                                  >
                                    <span>🔗</span>
                                    <span>Open Interactive Demo</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Other Categories - Standard Video Links */}
                      {category.id !== 'technology' && (
                        <>
                          {(categoryData.video_link || categoryData.video_showcase_url) && (
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                              <div className="flex items-start gap-3">
                                <div className="text-3xl">🎬</div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-blue-900 mb-1">Video Showcase</h4>
                                  <p className="text-sm text-blue-700 mb-3">Watch the project presentation and highlights</p>
                                  <a 
                                    href={categoryData.video_link || categoryData.video_showcase_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                  >
                                    <span>▶</span>
                                    <span>Watch Video</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {categoryData.virtual_tour_url && category.id !== 'technology' && (
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">🏛️</div>
                            <div className="flex-1">
                              <h4 className="font-bold text-purple-900 mb-1">Virtual Tour / 360° Experience</h4>
                              <p className="text-sm text-purple-700 mb-3">Explore the project in immersive 360°</p>
                              <a 
                                href={categoryData.virtual_tour_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                              >
                                <span>🔄</span>
                                <span>Take Virtual Tour</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Empty State */}
                {category.id === 'innovation' 
                  ? (allInnovationPhotos.length === 0 && !categoryData.video_link && !categoryData.video_showcase_url && !categoryData.virtual_tour_url && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📁</div>
                        <h3 className="text-xl font-bold text-gray-700 mb-2">No Files Available</h3>
                        <p className="text-gray-500">No photos, videos, or media files have been uploaded for this submission.</p>
                      </div>
                    ))
                  : category.id === 'technology'
                  ? (techSetupPhotos.length === 0 && techActionPhotos.length === 0 && uiScreenshots.length === 0 && !categoryData.demo_video_primary && !categoryData.demo_video_short && !categoryData.user_testing_video && !categoryData.virtual_demo_url && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📁</div>
                        <h3 className="text-xl font-bold text-gray-700 mb-2">No Files Available</h3>
                        <p className="text-gray-500">No photos, videos, or media files have been uploaded for this submission.</p>
                      </div>
                    ))
                  : (photos.length === 0 && !categoryData.video_link && !categoryData.virtual_tour_url && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">📁</div>
                        <h3 className="text-xl font-bold text-gray-700 mb-2">No Files Available</h3>
                        <p className="text-gray-500">No photos, videos, or media files have been uploaded for this submission.</p>
                      </div>
                    ))
                }
              </>
            )}
          </div>

          {/* Footer - Sticky Actions */}
          <div className="sticky bottom-0 bg-white border-t-2 border-gray-200 px-8 py-6 rounded-b-lg">
            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="border-gray-300"
                >
                  Close Preview
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    setTimeout(onEdit, 100);
                  }}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Submission
                </Button>
              </div>
              <Button
                onClick={onSubmit}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white px-8"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Lightbox for Images */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 flex items-center justify-between z-10">
            <div className="text-white font-semibold">
              Photo {lightboxIndex + 1} of {photos.length}
            </div>
            <div className="flex items-center gap-3">
              {/* Zoom Controls */}
              <button
                onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                title="Zoom Out (-)"
              >
                <span className="text-xl font-bold">−</span>
              </button>
              <span className="text-white text-sm min-w-[60px] text-center">{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                title="Zoom In (+)"
              >
                <span className="text-xl font-bold">+</span>
              </button>
              
              {/* Download Button */}
              <a
                href={lightboxImage}
                download={`photo-${lightboxIndex + 1}.jpg`}
                onClick={(e) => e.stopPropagation()}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </a>
              
              {/* Close Button */}
              <button
                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                title="Close (ESC)"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation Buttons */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              title="Previous (←)"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}
          
          {lightboxIndex < photos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              title="Next (→)"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={lightboxImage}
            alt={`Photo ${lightboxIndex + 1}`}
            className="max-w-full max-h-full object-contain transition-transform duration-200"
            style={{ transform: `scale(${zoomLevel})` }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Bottom Bar - Keyboard Shortcuts */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 z-10">
            <div className="flex items-center justify-center gap-6 text-white/70 text-sm">
              <span>← → : Navigate</span>
              <span>+ − : Zoom</span>
              <span>ESC : Close</span>
              <span>Space : Next</span>
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="absolute bottom-20 left-0 right-0 flex justify-center gap-2 z-10">
            {photos.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === lightboxIndex 
                    ? 'bg-orange-500 w-8' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                title={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4"
          onClick={() => setPdfViewerOpen(false)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">{pdfViewerName}</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = pdfViewerUrl;
                    link.download = pdfViewerName;
                    link.click();
                  }}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPdfViewerOpen(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* PDF Iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={pdfViewerUrl}
                className="w-full h-full border-0"
                title={pdfViewerName}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubmissionPreview;
